import React from 'react';
import { Container, Box, Typography, Grid } from '@mui/material';
import { styled } from '@mui/material/styles';

interface SMSPreviewProps {
  message: string;
  time: string;
  isReceived?: boolean;
}

const MessageBubble = styled(Box)<{ isReceived?: boolean }>(({ theme, isReceived }) => ({
  maxWidth: '70%',
  padding: '8px 12px',
  borderRadius: '18px',
  position: 'relative',
  marginBottom: '8px',
  ...(isReceived ? {
    backgroundColor: '#e9e9eb',
    alignSelf: 'flex-start',
    borderTopLeftRadius: '4px',
    color: '#000',
  } : {
    backgroundColor: '#007aff',
    alignSelf: 'flex-end',
    borderTopRightRadius: '4px',
    color: '#fff',
  }),
}));

const TimeStamp = styled(Typography)({
  fontSize: '11px',
  color: '#8e8e93',
  marginTop: '4px',
  textAlign: 'center',
});

const MessageContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  padding: '8px 16px',
});

const PhoneContainer = styled(Box)({
  height: '500px',
  backgroundColor: '#ffffff',
  border: '1px solid #e0e0e0',
  borderRadius: '12px',
  overflow: 'hidden',
  boxShadow: '0 2px 12px rgba(0,0,0,0.1)',
});

const IOSMessagePreview: React.FC<SMSPreviewProps> = ({ message, time, isReceived = true }) => {
  return (
    <MessageContainer>
      <MessageBubble isReceived={isReceived}>
        <Typography variant="body1" sx={{ fontSize: '16px', wordBreak: 'break-word' }}>
          {message}
        </Typography>
      </MessageBubble>
      <TimeStamp>{time}</TimeStamp>
    </MessageContainer>
  );
};

const SMSPreviewPage: React.FC = () => {
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom align="center" sx={{ mb: 4 }}>
        TBX HealthTech SMS Preview System
      </Typography>

      <Grid container spacing={3}>
        {/* Patient Messages */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PhoneContainer>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#f2f2f7',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                  TBX HealthTech
                </Typography>
              </Box>

              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                bgcolor: '#ffffff',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <TimeStamp sx={{ my: 2 }}>Today 8:00 AM</TimeStamp>
                
                <IOSMessagePreview
                  message="Time to take Rifampicin (600mg). Please take your medication now. Have a great day!"
                  time="8:00 AM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 2:00 PM</TimeStamp>

                <IOSMessagePreview
                  message="Time to take Isoniazid (300mg). Please take your medication now. Stay strong!"
                  time="2:00 PM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 6:00 PM</TimeStamp>

                <IOSMessagePreview
                  message="Time to take Pyrazinamide (1600mg). Please take your medication now. Keep going!"
                  time="6:00 PM"
                  isReceived={true}
                />
              </Box>
            </Box>
          </PhoneContainer>
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 2, 
              color: 'primary.main',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            Patient Messages
          </Typography>
        </Grid>

        {/* Caregiver Messages */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PhoneContainer>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#f2f2f7',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                  TBX HealthTech
                </Typography>
              </Box>

              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                bgcolor: '#ffffff',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <TimeStamp sx={{ my: 2 }}>Today 8:00 AM</TimeStamp>

                <IOSMessagePreview
                  message="Please remind your patient to take Rifampicin (600mg) now. Your support helps ensure successful treatment. 🙂"
                  time="8:00 AM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 2:00 PM</TimeStamp>

                <IOSMessagePreview
                  message="Time for your patient's Isoniazid (300mg). Please ensure they take their medication. Thank you for your care! 🙂"
                  time="2:00 PM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 6:00 PM</TimeStamp>

                <IOSMessagePreview
                  message="Evening medication reminder: Pyrazinamide (1600mg). Please assist your patient with their treatment. 🙂"
                  time="6:00 PM"
                  isReceived={true}
                />
              </Box>
            </Box>
          </PhoneContainer>
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 2, 
              color: 'primary.main',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            Caregiver Messages
          </Typography>
        </Grid>

        {/* TB Admin Messages */}
        <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <PhoneContainer>
            <Box sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}>
              <Box sx={{
                p: 2,
                borderBottom: '1px solid #e0e0e0',
                bgcolor: '#f2f2f7',
                textAlign: 'center'
              }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#000000' }}>
                  TBX HealthTech
                </Typography>
              </Box>

              <Box sx={{ 
                flex: 1, 
                overflowY: 'auto',
                bgcolor: '#ffffff',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
              }}>
                <TimeStamp sx={{ my: 2 }}>Today 8:15 AM</TimeStamp>

                <IOSMessagePreview
                  message="Patient ID#12345 has taken their morning medication"
                  time="8:15 AM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 2:10 PM</TimeStamp>

                <IOSMessagePreview
                  message="Patient ID#13579 has taken their afternoon medication"
                  time="2:10 PM"
                  isReceived={true}
                />

                <TimeStamp sx={{ my: 2 }}>Today 6:05 PM</TimeStamp>

                <IOSMessagePreview
                  message="Patient ID#24680 has taken their evening medication"
                  time="6:05 PM"
                  isReceived={true}
                />
              </Box>
            </Box>
          </PhoneContainer>
          <Typography 
            variant="h6" 
            sx={{ 
              mt: 2, 
              color: 'primary.main',
              fontWeight: 500,
              textAlign: 'center'
            }}
          >
            TB Admin Notifications
          </Typography>
        </Grid>
      </Grid>
    </Container>
  );
};

export default SMSPreviewPage; 